import { type ReactElement, useState } from "react"

import { useTranslation } from "react-i18next"
import { useMutation } from "react-query"
import { useDispatch, useSelector } from "react-redux"

import * as Yup from "yup"
import { Button, InputText, NotificationUtils, Typography } from "@synapse-analytics/synapse-ui"
import type { AxiosError, AxiosResponse } from "axios"
import { useFormik } from "formik"

import { InfoBlock } from "../../../components/containers/InfoBlock"
import { SidAPI } from "../../../services/SidAPI"
import type { RootState } from "../../../store/ReduxStore"
import type { User } from "../../../types/generated/authentication/User"
import { setFirstName, setLastName } from "../../Auth/Slice"
import { InfoRow } from "./InfoRow"

import styles from "../Settings.module.scss"

/**
 * `AccountForm` is a settings component that displays and allows editing of the
 * current user's account information, including first name and last name.
 *
 * @returns A ReactElement displaying editable user account information.
 */
export function AccountForm(): ReactElement {
  const { t } = useTranslation("settings")
  const dispatch = useDispatch()

  const [isInEditMode, setIsInEditMode] = useState(false)

  const updateUserMutation = useMutation<AxiosResponse<User>, AxiosError, Partial<User>>(SidAPI.updateUser, {
    onSuccess: (response) => {
      NotificationUtils.toast(t("accountForm.updateUser.success"), { severity: "success" })
      dispatch(setFirstName(response.data.first_name))
      dispatch(setLastName(response.data.last_name))
      formik.resetForm({
        values: {
          first_name: response.data.first_name,
          last_name: response.data.last_name ?? "",
        },
      })
      setIsInEditMode(false)
    },
    onError: () => {
      NotificationUtils.toast(t("accountForm.updateUser.error"), { severity: "error" })
    },
  })

  const formik = useFormik({
    initialValues: {
      first_name: useSelector((state: RootState) => state.auth.firstName) ?? "",
      last_name: useSelector((state: RootState) => state.auth.lastName) ?? "",
    },
    validationSchema: Yup.object({
      first_name: Yup.string().trim().min(1, "First name is required").required("First name is required"),
    }),
    onSubmit: (values) => {
      updateUserMutation.mutate({
        first_name: values.first_name,
        last_name: values.last_name,
      })
    },
  })

  return (
    <div className={styles.root}>
      <div className={styles.header}>
        <Typography variant="h2-bold">{t("accountForm.title")}</Typography>

        {isInEditMode ? (
          <div className={styles.cancelSave}>
            <Button
              variant="ghost-secondary"
              size="small"
              disabled={updateUserMutation.isLoading}
              onClick={() => {
                formik.resetForm()
                setIsInEditMode(false)
              }}
            >
              {t("buttons.cancel")}
            </Button>

            <Button
              variant="primary"
              size="small"
              disabled={updateUserMutation.isLoading || !formik.dirty || !formik.isValid}
              isLoading={updateUserMutation.isLoading}
              onClick={() => {
                formik.handleSubmit()
              }}
            >
              {t("buttons.save")}
            </Button>
          </div>
        ) : (
          <Button variant="ghost" size="small" onClick={() => setIsInEditMode(true)}>
            {t("buttons.edit")}
          </Button>
        )}
      </div>

      <InfoRow rowTitle={t("accountForm.firstName")} className={styles.infoBlock}>
        {isInEditMode ? (
          <InputText
            fullWidth
            hideDescription
            variant={"outlined"}
            placeholder={t("accountForm.firstName")}
            id="first_name"
            disabled={updateUserMutation.isLoading}
            value={formik.values.first_name}
            handleChange={formik.handleChange}
            error={formik.errors.first_name}
          />
        ) : (
          <InfoBlock text={formik.values.first_name ?? ""} type="default" />
        )}
      </InfoRow>

      <InfoRow rowTitle={t("accountForm.lastName")} className={styles.infoBlock}>
        {isInEditMode ? (
          <InputText
            fullWidth
            hideDescription
            variant={"outlined"}
            placeholder={t("accountForm.lastName")}
            id="last_name"
            disabled={updateUserMutation.isLoading}
            value={formik.values.last_name}
            handleChange={formik.handleChange}
          />
        ) : (
          <InfoBlock text={formik.values.last_name ?? ""} type="default" />
        )}
      </InfoRow>

      <InfoRow rowTitle={t("accountForm.email")} className={styles.infoBlock}>
        <InfoBlock
          text={useSelector((state: RootState) => state.auth.email) ?? ""}
          textTransform={"lowercase"}
          type="default"
        />
      </InfoRow>
    </div>
  )
}
